// @ts-check
// ====== eventBus — pub/sub interno per disaccoppiare i moduli ======
// Sostituisce le chiamate cross-module hardcoded (es. renderDashboard() chiamato
// da clienti.js, audit.js, ecc.) con eventi semantici emessi sul bus.
//
// Pattern di adozione progressivo:
//   - I moduli emettono eventi su `bus.emit('clienti:changed', payload)`.
//   - I render layer si registrano con `bus.on('clienti:changed', cb)`.
//   - I `globalThis.*` esistenti restano per compatibilità.
//
// Eventi standard:
//   'state:loaded'           snapshot loadStateFromSupabase appena terminato
//   'clienti:changed'        un cliente è stato create/update/delete (paylod: { nome, action })
//   'accessi:changed'        accesso aggiornato (payload: { cliente, risorsa, stato })
//   'scadenze:changed'       scadenza creata/modificata/snoozed
//   'audit:appended'         nuova voce di audit log
//   'view:changed'           switch di vista (payload: { view, prev })
//   'auth:login' / 'auth:logout'
//   'notifications:new'      nuova notifica ricevuta (realtime)

/** @typedef {(payload?: any) => void | Promise<void>} BusHandler */

const _listeners = new Map(); // event -> Set<handler>

/** @type {{
 *   on(event: string, handler: BusHandler): () => void;
 *   off(event: string, handler: BusHandler): void;
 *   emit(event: string, payload?: any): void;
 *   once(event: string, handler: BusHandler): () => void;
 *   clear(event?: string): void;
 *   eventNames(): string[];
 * }} */
const bus = {
  on(event, handler) {
    if (typeof handler !== 'function') throw new Error('handler must be a function');
    let set = _listeners.get(event);
    if (!set) {
      set = new Set();
      _listeners.set(event, set);
    }
    set.add(handler);
    return () => bus.off(event, handler);
  },
  off(event, handler) {
    const set = _listeners.get(event);
    if (set) set.delete(handler);
  },
  emit(event, payload) {
    const set = _listeners.get(event);
    if (!set || !set.size) return;
    // Snapshot per consentire on/off durante emit
    [...set].forEach((h) => {
      try {
        const r = h(payload);
        if (r && typeof r.then === 'function') r.catch((e) => console.error('[bus]', event, e));
      } catch (e) {
        console.error('[bus]', event, e);
      }
    });
  },
  once(event, handler) {
    const wrapper = (p) => {
      bus.off(event, wrapper);
      handler(p);
    };
    return bus.on(event, wrapper);
  },
  clear(event) {
    if (event) _listeners.delete(event);
    else _listeners.clear();
  },
  eventNames() {
    return [...(_listeners.keys() || [])];
  }
};

// @ts-ignore — esposizione globale temporanea durante la migrazione
globalThis.bus = bus;
export { bus };
